/**
 * Maps between API `emergencyContactAddress` (newline-delimited string) and form fields.
 *
 * Stored format (empty intermediate lines may be omitted):
 *   line1
 *   line2
 *   city, state, zip
 *   country
 */
export interface EmergencyContactAddressFields {
  emergencyAddressLine1: string;
  emergencyAddressLine2: string;
  emergencyCity: string;
  emergencyState: string;
  emergencyZip: string;
  emergencyCountry: string;
}

const ZIP_PATTERN = /\d{5}(-\d{4})?/;

function parseCityStateZipLine(line: string): { city: string; state: string; zip: string } | null {
  const segments = line.split(',').map((segment) => segment.trim()).filter(Boolean);
  if (segments.length >= 3) {
    return {
      city: segments[0] || '',
      state: segments[1] || '',
      zip: segments.slice(2).join(', ') || '',
    };
  }
  if (segments.length === 2) {
    const stateZip = segments[1].split(/\s+/).filter(Boolean);
    const zip = stateZip.slice(1).join(' ');
    if (ZIP_PATTERN.test(zip)) {
      return {
        city: segments[0] || '',
        state: stateZip[0] || '',
        zip,
      };
    }
  }
  return null;
}

export function parseEmergencyContactAddress(
  address?: string | null,
  defaultCountry = 'USA'
): EmergencyContactAddressFields {
  const empty: EmergencyContactAddressFields = {
    emergencyAddressLine1: '',
    emergencyAddressLine2: '',
    emergencyCity: '',
    emergencyState: '',
    emergencyZip: '',
    emergencyCountry: defaultCountry,
  };

  const raw = (address || '').trim();
  if (!raw) {
    return empty;
  }

  const lines = raw.split('\n').map((line) => line.trim());
  const cszIndex = lines.findIndex((line) => parseCityStateZipLine(line) !== null);
  const parsed = cszIndex >= 0 ? parseCityStateZipLine(lines[cszIndex]) : null;

  if (parsed) {
    const before = lines.slice(0, cszIndex);
    const after = lines.slice(cszIndex + 1);
    return {
      emergencyAddressLine1: before[0] || '',
      emergencyAddressLine2: before[1] || '',
      emergencyCity: parsed.city,
      emergencyState: parsed.state,
      emergencyZip: parsed.zip,
      emergencyCountry: after[0] || defaultCountry,
    };
  }

  return {
    emergencyAddressLine1: lines[0] || '',
    emergencyAddressLine2: lines[1] || '',
    emergencyCity: '',
    emergencyState: '',
    emergencyZip: '',
    emergencyCountry: lines[2] || defaultCountry,
  };
}

export function formatEmergencyContactAddress(
  fields: Partial<EmergencyContactAddressFields>
): string | null {
  const line1 = fields.emergencyAddressLine1?.trim() || '';
  const line2 = fields.emergencyAddressLine2?.trim() || '';
  const city = fields.emergencyCity?.trim() || '';
  const state = fields.emergencyState?.trim() || '';
  const zip = fields.emergencyZip?.trim() || '';
  const country = fields.emergencyCountry?.trim() || '';

  const cityStateZip = [city, state, zip].filter(Boolean).join(', ');
  const parts = [line1, line2, cityStateZip, country].filter(Boolean);

  return parts.length > 0 ? parts.join('\n') : null;
}
